// src/stores/cart.js
import { browser } from '$app/environment';
import { writable, derived, get } from 'svelte/store';
import { persist, createLocalStorage } from '@macfja/svelte-persistent-store';

import { auth } from '$lib/auth';
import { addCart } from '$lib/firestore/addCart';
import { updateCart } from '$lib/firestore/updateCart';
import { logError } from '$lib/helpers/errors';

/* ─────────────── Persisted & basic stores ─────────────── */
export const cartContents = persist(writable([]), createLocalStorage(), 'cartStore');
// If persisted data isn't an array (e.g. older version), attempt to coerce it
if (browser) {
    const persisted = get(cartContents);
    if (!Array.isArray(persisted)) {
        try {
            let coerced = typeof persisted === 'string'
                ? JSON.parse(persisted)
                : Object.values(persisted);
            if (!Array.isArray(coerced)) {
                coerced = [];
            }
            // Filter out invalid cart items that don't have an "id" property (e.g. "#$@__reference__0")
            const validItems = coerced.filter(item => typeof item === 'object' && item !== null && item.hasOwnProperty('id'));
            cartContents.set(validItems);
        } catch {
            cartContents.set([]);
        }
    }
}
export const userCodeDiscount = persist(writable(''), createLocalStorage(), 'userCodeDiscount');
export const usedDiscountCode = persist(writable(''), createLocalStorage(), 'usedDiscountCode');

export const pointStore = writable(0);
export const cartDocumentId = writable('');

export const paymentIntentId = persist(        // keep it across reloads
    writable(''),
    createLocalStorage(),
    'paymentIntentId'
);

/* ─────────────── Simple helpers ─────────────── */
export const cartTotal = derived(cartContents, ($cart) => {
    if (!Array.isArray($cart)) return 0;
    return $cart.reduce((t, item) => t + parseFloat(item?.cost ?? 0), 0);
});

export function calculateDiscount(total, perc) {
    if (!total || !perc) return { error: 'Missing total or discount' };
    return (total - total * (perc / 100)).toFixed(2);
}

export const cartDiscount = derived(cartContents, () => null);   // multi‑buy disabled

export const ticketTotal = derived(cartContents, ($cart) => {
    if (!Array.isArray($cart)) {
        userCodeDiscount.set('');
        usedDiscountCode.set('');
        return 0;
    }
    const total = $cart.reduce((t, i) => t + Number(i.quantity), 0);
    if (!$cart.length) {
        userCodeDiscount.set('');
        usedDiscountCode.set('');
    }
    return total;
});

/* ─────────────── Create / update the cart doc ─────────────── */
let cartCreationInFlight = null;

export async function createOrUpdateCart() {
    if (!browser) return null;

    const contents = get(cartContents);
    const summary = get(theCart);
    let docId = get(cartDocumentId);
    let uid = get(auth)?.user?.uid || 'guest';

    const data = {
        contents,
        theCart: summary,
        paymentIntentId: get(paymentIntentId) || ''
    };

    try {
        if (docId) {
            await updateCart(docId, data);
            return docId;
        }

        if (cartCreationInFlight) return cartCreationInFlight;

        cartCreationInFlight = addCart(uid, data)
            .then((newId) => {
                cartDocumentId.set(newId || '');
                return newId;
            })
            .finally(() => (cartCreationInFlight = null));

        return cartCreationInFlight;
    } catch (err) {
        console.error('createOrUpdateCart error:', err);
        logError(err, 'createOrUpdateCart');
        return null;
    }
}

/* ─────────────── Derived “summary” used by checkout ─────────────── */
export const theCart = derived(
    [cartContents, cartTotal, cartDiscount, ticketTotal, userCodeDiscount, pointStore],
    ([$cart, $total, $disc, $qty, $userDisc, $pts]) => {
        if (!browser) return;

        const obj = {
            ticketTotal: $qty,
            originalTotal: $total,
            ticketBundles: $disc ? { bundleTotal: $disc.newTotal, bundleDiscountAmount: $disc.discountAmount } : {},
            userDiscount: $userDisc,
            hasDiscount: false,
            totalAmountSaved: '0',
            printTotal: 0,
            intentTotal: 0          // pence
        };

        let intent = $userDisc ? Number($userDisc)
            : Number(obj.ticketBundles.bundleTotal) || Number($total);

        obj.printTotal = Number(intent.toFixed(2));
        obj.intentTotal = Number((intent * 100).toFixed(0));

        /* points redemption */
        if ($pts > 0) {
            const newVal = $pts < obj.printTotal ? (obj.printTotal - $pts).toFixed(2) : 0;
            obj.intentTotal = Number(newVal) * 100;
            obj.printTotal = obj.intentTotal / 100;
        }

        /* did we discount? */
        if (obj.originalTotal !== obj.printTotal) {
            obj.hasDiscount = true;
            obj.totalAmountSaved = (obj.originalTotal - obj.printTotal).toFixed(2);
        }

        /* empty cart → reset */
        if (obj.ticketTotal === 0) {
            obj.userDiscount = '';
            obj.hasDiscount = false;
            obj.printTotal = 0;
            obj.intentTotal = 0;
            usedDiscountCode.delete();
        }

        return obj;
    }
);
